import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getTPRMPlatformVendorsApi } from "../../../services/axios/AuthApis/TPRMApis";
import DataTable from "../../common/DataTable";
import PageLoader from "../../ui/PageLoader";
import { formatTPRMTimestamp } from "./tprmPageUtils";

const PAGE_SIZE = 20;
const filterOptions = (values) =>
  values.map((value) => ({ value, label: value }));

const PlatformVendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [columnFilters, setColumnFilters] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  const queryFilters = useMemo(
    () => Object.fromEntries(columnFilters.map(({ id, value }) => [id, value])),
    [columnFilters],
  );

  useEffect(() => {
    let cancelled = false;
    const loadVendors = async () => {
      setLoading(true);
      setError("");
      const [createdFrom, createdTo] = queryFilters.created_at || [];
      try {
        const response = await getTPRMPlatformVendorsApi({
          customer: queryFilters.customer_name,
          vendor_name: queryFilters.vendor_name,
          industry: queryFilters.industry,
          criticality: queryFilters.business_criticality,
          risk_tier: queryFilters.assessment_risk_tier,
          status: queryFilters.status,
          created_from: createdFrom,
          created_to: createdTo,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
        });
        if (!cancelled) {
          setVendors(Array.isArray(response?.results) ? response.results : []);
          setCount(Number(response?.count) || 0);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(
            requestError.response?.data?.detail ||
              requestError.message ||
              "Unable to load the cross-customer vendor register.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadVendors();
    return () => {
      cancelled = true;
    };
  }, [pagination, queryFilters, reloadKey]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "vendor_name",
        header: "Vendor",
        meta: {
          filter: {
            variant: "text",
            label: "Vendor name",
            placeholder: "Filter vendor",
          },
        },
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.vendor_name || "Unnamed vendor"}</div>
            <div className="text-xs text-slate-500">{row.original.vendor_id || "—"}</div>
          </div>
        ),
      },
      {
        accessorKey: "customer_name",
        header: "Customer / company",
        meta: {
          filter: {
            variant: "text",
            label: "Customer",
            placeholder: "Filter customer",
          },
        },
        cell: ({ row }) => (
          <div>
            <span>{row.original.customer_name || "Unverified / unavailable"}</span>
            {row.original.customer_mapping_status !== "verified" && (
              <div className="text-xs text-amber-700">
                {row.original.customer_mapping_status}
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: "industry",
        header: "Industry",
        meta: {
          filter: {
            variant: "text",
            label: "Industry",
            placeholder: "Filter industry",
          },
        },
        cell: ({ getValue }) => getValue() || "—",
      },
      {
        accessorKey: "website",
        header: "Website / domain",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const website = getValue();
          if (!website) return "—";
          try {
            const url = new URL(website);
            return (
              <a
                href={url.href}
                target="_blank"
                rel="noreferrer"
                className="text-blue-700 underline"
              >
                {url.hostname}
              </a>
            );
          } catch {
            return website;
          }
        },
      },
      {
        accessorKey: "business_criticality",
        header: "Business criticality",
        meta: {
          filter: {
            variant: "select",
            label: "Business criticality",
            placeholder: "All criticality",
            options: filterOptions(["Low", "Medium", "High", "Critical", "Mixed", "Unavailable"]),
          },
        },
      },
      {
        accessorKey: "assessment_risk_tier",
        header: "Assessment risk tier",
        meta: {
          filter: {
            variant: "select",
            label: "Assessment risk tier",
            placeholder: "All risk tiers",
            options: filterOptions(["Critical", "High", "Medium", "Low", "Unassessed"]),
          },
        },
        cell: ({ row }) => (
          <span>
            {row.original.assessment_risk_tier || "Unassessed"}
            {row.original.assessment_risk_score == null
              ? ""
              : ` (${row.original.assessment_risk_score})`}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Lifecycle status",
        meta: {
          filter: {
            variant: "select",
            label: "Lifecycle status",
            placeholder: "All statuses",
            options: filterOptions([
              "Draft",
              "Active",
              "Inactive",
              "Under Review",
              "Onboarding",
              "Pending Assessment",
              "Pending Approval",
              "Suspended",
              "Offboarding",
              "Offboarded",
              "Rejected",
            ]),
          },
        },
      },
      {
        accessorKey: "created_at",
        header: "Created",
        meta: {
          filter: {
            variant: "date-range",
            label: "Created date",
          },
        },
        cell: ({ getValue }) => formatTPRMTimestamp(getValue()),
      },
      {
        accessorKey: "updated_at",
        header: "Updated",
        enableColumnFilter: false,
        cell: ({ getValue }) => formatTPRMTimestamp(getValue()),
      },
    ],
    [],
  );

  const handlePaginationChange = useCallback((updater) => {
    setPagination((current) =>
      typeof updater === "function" ? updater(current) : updater,
    );
  }, []);
  const handleColumnFiltersChange = useCallback((updater) => {
    setColumnFilters((current) =>
      typeof updater === "function" ? updater(current) : updater,
    );
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, []);

  if (loading && !vendors.length && !error) {
    return <PageLoader title="Loading platform vendor register..." minH="min-h-[35vh]" />;
  }

  return (
    <section className="space-y-4" aria-labelledby="platform-tprm-vendors-heading">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2
            id="platform-tprm-vendors-heading"
            className="text-xl font-semibold text-slate-900 dark:text-white"
          >
            Cross-customer vendor register
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Read-only portfolio view. Filters and pagination are applied by the server;
            sorting is disabled because the endpoint does not expose ordering.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setReloadKey((key) => key + 1)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium"
        >
          Refresh
        </button>
      </div>
      {error && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" role="alert">
          {error}
        </p>
      )}
      <DataTable
        columns={columns}
        data={vendors}
        loading={loading}
        error={null}
        emptyMessage="No vendors match the current filters."
        enableSorting={false}
        enableGlobalFilter={false}
        enableColumnFilters
        enableColumnVisibility={false}
        manualPagination
        manualFiltering
        rowCount={count}
        pageCount={Math.ceil(count / pagination.pageSize)}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        className="min-w-0 [&_.overflow-x-auto]:dark:border-slate-700 [&_.overflow-x-auto]:dark:bg-slate-900 [&_thead]:dark:bg-slate-800 [&_thead]:dark:text-slate-300 [&_th]:dark:border-slate-700 [&_tbody_tr]:dark:hover:bg-slate-800 [&_tbody_td]:dark:text-slate-300 [&_td]:dark:border-slate-800"
        getRowId={(vendor) => vendor.id}
      />
    </section>
  );
};

export default PlatformVendorsPage;
